import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  getAllAppointments,
  getAppointmentDetails,
  updateAppointment,
} from "../apis/Appointment";
import { toast } from "react-toastify";
import { SlotTimeDisplay } from "../constants/WeekDays";

const Appointments = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAppointment, setEditedAppointment] = useState(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (isMounted) {
      fetchAppointments();
    }
  }, [page, rowsPerPage, filterStatus, isMounted]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await getAllAppointments(
        page,
        rowsPerPage,
        filterStatus
      );
      if (isMounted) {
        setAppointments(response.data);
        setTotalCount(response.meta.totalCount);
      }
    } catch (error) {
      toast.error("Failed to fetch appointments");
      console.error("Error fetching appointments:", error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = async (appointment) => {
    try {
      const details = await getAppointmentDetails(appointment.id);
      setSelectedAppointment(details);
      setOpenDialog(true);
    } catch (error) {
      toast.error("Failed to fetch appointment details");
      console.error("Error fetching appointment details:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAppointment(null);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedAppointment({ ...selectedAppointment });
  };

  const handleSave = async () => {
    try {
      await updateAppointment(selectedAppointment.id, editedAppointment);
      toast.success("Appointment updated successfully");
      setIsEditing(false);
      setSelectedAppointment(editedAppointment);
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to update appointment");
      console.error("Error updating appointment:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedAppointment(null);
  };

  const handleInputChange = (field, value) => {
    setEditedAppointment((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getSlotTitle = (time) => {
    return SlotTimeDisplay.find((item) => item.value === time)?.title;
  };

  const getStatusChip = (status) => {
    const statusColors = {
      paid: "success",
      pending: "warning",
    };

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={statusColors[status.toLowerCase()]}
        size="small"
        sx={{
          fontWeight: "medium",
          minWidth: "85px",
        }}
      />
    );
  };

  if (loading && appointments.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={filterStatus}
              label="Payment Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Appointment Date</TableCell>
                <TableCell>Slot Time</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Payment Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map((appointment) => (
                <TableRow
                  key={appointment.id}
                  hover
                  onClick={() => handleRowClick(appointment)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{appointment.name}</TableCell>
                  <TableCell>{appointment.phone}</TableCell>
                  <TableCell>
                    {new Date(appointment.date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getSlotTitle(appointment.slotTime)}</TableCell>
                  <TableCell>
                    {new Date(appointment.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{getStatusChip(appointment.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Appointment Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Appointment Details
            {!isEditing && (
              <IconButton onClick={handleEditClick} color="primary">
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Box sx={{ mt: 2, maxWidth: "500px", margin: "0 auto" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Name:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedAppointment.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      sx={{ width: "300px" }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {selectedAppointment.name}
                    </Typography>
                  )}
                </Box>

                {selectedAppointment.email && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                    >
                      Email:
                    </Typography>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editedAppointment.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        sx={{ width: "300px" }}
                      />
                    ) : (
                      <Typography variant="body1">
                        {selectedAppointment.email}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Phone:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedAppointment.phone}
                      onChange={(e) =>
                        handleInputChange("phone", e.target.value)
                      }
                      sx={{ width: "300px" }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {selectedAppointment.phone}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Age:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      type="number"
                      value={editedAppointment.age}
                      onChange={(e) => handleInputChange("age", e.target.value)}
                      sx={{ width: "300px" }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {selectedAppointment.age}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Gender:
                  </Typography>
                  {isEditing ? (
                    <FormControl size="small" sx={{ width: "300px" }}>
                      <Select
                        value={editedAppointment.gender}
                        onChange={(e) =>
                          handleInputChange("gender", e.target.value)
                        }
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body1">
                      {selectedAppointment.gender}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Appointment:
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedAppointment.date).toLocaleDateString()} -{" "}
                    {selectedAppointment.slotTime}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Status:
                  </Typography>
                  {getStatusChip(selectedAppointment.status)}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    City:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editedAppointment.city}
                      onChange={(e) =>
                        handleInputChange("city", e.target.value)
                      }
                      sx={{ width: "300px" }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {selectedAppointment.city}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Booked At:
                  </Typography>
                  <Typography variant="body1">
                    {new Date(
                      selectedAppointment.createdAt
                    ).toLocaleDateString()}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: "100px", fontWeight: "bold", mr: 3 }}
                  >
                    Description:
                  </Typography>
                  {isEditing ? (
                    <TextField
                      multiline
                      rows={3}
                      value={editedAppointment.description || ""}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      sx={{ width: "300px" }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedAppointment.description ||
                        "No description provided"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isEditing ? (
            <>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointments;
